import type { FC, ReactNode } from 'react'

import { useState } from 'react'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/anim'

type Billing = 'yearly' | 'monthly'

const featureBlurb = 'For anyone validating Framer as a professional prototyping tool.'

export const PricingSection: FC = (): ReactNode => {
    const [billing, setBilling] = useState<Billing>('yearly')

    const pro = billing === 'yearly'
        ? { amount: '$100', period: '/year' }
        : { amount: '$20', period: '/month' }

    return (
        <div id='pricing' className='section overflow-hidden'>
            <div className='container no-padding-top-mobile'>
                <div>
                    <div className='flex-center-text'>
                        <Reveal as='h2' className='hero-text'>
                            <span className='gradient-text'>Pricing</span> for everyone
                        </Reveal>
                        <div className='fixed-text grey-text'>
                            Empower your users by building cards, accounts, payments, and lending products
                            — as easy as plug-and-play.
                        </div>
                    </div>

                    <div className='gradient-center _03' />

                    <div className='tabs w-tabs'>
                        <div className='filter-holder w-tab-menu' role='tablist'>
                            <a
                                role='tab'
                                aria-selected={billing === 'yearly'}
                                className={cn('category-item-link w-inline-block w-tab-link', billing === 'yearly' && 'w--current')}
                                onClick={() => setBilling('yearly')}
                            >
                                <div>
                                    Bill yearly<span className='yearly-link-discount'> -15%</span>
                                </div>
                            </a>
                            <a
                                role='tab'
                                aria-selected={billing === 'monthly'}
                                className={cn('category-item-link w-inline-block w-tab-link', billing === 'monthly' && 'w--current')}
                                onClick={() => setBilling('monthly')}
                            >
                                <div>Bill monthly</div>
                            </a>
                        </div>

                        <div className='tabs-content w-tab-content'>
                            <div className='tab-pane w-tab-pane w--tab-active'>
                                <div className='w-layout-grid pricing-grid'>
                                    <div id='w-node-_6507fa67-d7e9-e8b5-61b9-e576becfb1ea-c63dcf76' className='content'>
                                        <div className='pricing-item'>
                                            <div className='pricing-type'>Starter</div>
                                            <div className='price-holder'>
                                                <div className='price'>$0</div>
                                            </div>
                                            <div className='pricing-features-holders'>
                                                <div className='pricing-feature-item'>
                                                    <div className='pricing-feature-text'>{featureBlurb}</div>
                                                </div>
                                            </div>
                                            <ul role='list' className='list'>
                                                <li className='list-item-with-checkicon'>Premium templates</li>
                                                <li className='list-item-with-checkicon'>Up to 2 editors</li>
                                                <li className='list-item-with-checkicon'>Up to 3 projects</li>
                                            </ul>
                                            <div className='pricing-button-holder'>
                                                <a href='#' className='button-gradient-nav non-capitalize w-inline-block'>
                                                    <div>Sign up for free</div>
                                                    <div className='button-gradient-bg' />
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <div id='w-node-_6507fa67-d7e9-e8b5-61b9-e576becfb20c-c63dcf76' className='content'>
                                        <div className='pricing-type-active'>
                                            <div className='pricing-item'>
                                                <div className='pricing-type'>Pro</div>
                                                <div className='price-holder price-holder-active'>
                                                    <div className='price'>
                                                        <span>
                                                            {pro.amount}
                                                            <sup />
                                                        </span>
                                                    </div>
                                                    <div className='light-grey'>{pro.period}</div>
                                                </div>
                                                <div className='pricing-features-holders active-pricing-features'>
                                                    <div className='pricing-feature-text'>{featureBlurb}</div>
                                                </div>
                                                <ul role='list' className='list'>
                                                    <li className='list-item-with-checkicon active-pricing-list-item'>Everything in Starter +</li>
                                                    <li className='list-item-with-checkicon active-pricing-list-item'>Up to 2 editors</li>
                                                    <li className='list-item-with-checkicon active-pricing-list-item'>Up to 3 projects</li>
                                                    <li className='list-item-with-checkicon active-pricing-list-item'>Anonymous board viewers</li>
                                                    <li className='list-item-with-checkicon active-pricing-list-item'>Up to 2 editors</li>
                                                </ul>
                                                <div className='pricing-button-holder'>
                                                    <a href='#' className='button-gradient-nav non-capitalize w-inline-block'>
                                                        <div>Subscribe</div>
                                                        <div className='button-gradient-bg' />
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div id='w-node-_6507fa67-d7e9-e8b5-61b9-e576becfb231-c63dcf76' className='content'>
                                        <div className='pricing-item'>
                                            <div className='pricing-type'>Enterprise</div>
                                            <div className='pricing-features-holders'>
                                                <div className='price-holder'>
                                                    <div className='price'>
                                                        Custom<sup />
                                                    </div>
                                                </div>
                                                <div className='pricing-feature-item'>
                                                    <div className='pricing-feature-text'>{featureBlurb}</div>
                                                </div>
                                            </div>
                                            <ul role='list' className='list'>
                                                <li className='list-item-with-checkicon'>Everything in Teams +</li>
                                                <li className='list-item-with-checkicon'>Up to 2 editors</li>
                                                <li className='list-item-with-checkicon'>Weekly Reports</li>
                                            </ul>
                                            <div className='pricing-button-holder'>
                                                <a href='#' className='button-gradient-nav non-capitalize w-inline-block'>
                                                    <div>Contact Sales</div>
                                                    <div className='button-gradient-bg' />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
